// ============================================================
// useLivePermissions.ts — make an open screen follow a LIVE permission change
// (admin changes this user's role / permissions / the firm defaults → the
// server pushes 'permissions-changed' → App re-reads /api/auth/me →
// store.permissionsVersion goes up). The SERVER stays the real gatekeeper;
// this only keeps what's on screen honest without a refresh.
//
//   useLivePermissions((narrowed) => { if (narrowed) clearMoney(); void reload() })
//
// `narrowed` is true when this user just LOST a way of seeing colleagues'
// money (admin, "See colleagues' billing", "View all billing") — the screen
// should clear any £ already shown AT ONCE (don't wait for the reload), then
// reload under the new rights. Granting access takes the same path (narrowed
// false → just reload). Never fires on mount.
// ============================================================
import { useEffect, useRef } from 'react'
import { useCasevoStore, type AuthUser } from '../store/useCasevoStore'

type BillingAccess = { admin: boolean; seeColleaguesBilling: boolean; viewAllBilling: boolean }
export const billingAccessOf = (u: Pick<AuthUser, 'role' | 'effectivePermissions'> | null | undefined): BillingAccess => {
  const p = (u?.effectivePermissions ?? {}) as Record<string, boolean | undefined>
  return { admin: u?.role === 'admin', seeColleaguesBilling: !!p.seeColleaguesBilling, viewAllBilling: !!p.viewAllBilling }
}
// Did any money-visibility right go from ON to OFF?
export const billingAccessNarrowed = (prev: BillingAccess, next: BillingAccess): boolean =>
  (prev.admin && !next.admin) || (prev.seeColleaguesBilling && !next.seeColleaguesBilling && !next.admin) || (prev.viewAllBilling && !next.viewAllBilling && !next.admin)

export function useLivePermissions(onChange: (narrowed: boolean) => void): void {
  const version = useCasevoStore((s) => s.permissionsVersion)
  const authUser = useCasevoStore((s) => s.authUser)
  const seen = useRef(version)
  const access = useRef(billingAccessOf(authUser))
  const cb = useRef(onChange)
  cb.current = onChange
  useEffect(() => {
    if (seen.current === version) return
    seen.current = version
    const next = billingAccessOf(useCasevoStore.getState().authUser)
    const narrowed = billingAccessNarrowed(access.current, next)
    access.current = next
    cb.current(narrowed)
  }, [version])
}
